import { mutateUsers, users } from "@/api/users/useUsersQuery";
import { usersHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { EquipmentDetailForm } from "@/features/equipment/components/form/EquipmentDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { UsersUpdateForm } from "@/features/users/components/form/UsersUpdateForm";
import { useSub } from "@/hooks";
import useQueryString from "@/hooks/useQueryString";
import useUpdateUrlParams from "@/hooks/useUpdateUrlParams";
import { theme } from "@/styles/theme";
import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, Flex, Pagination, useMantineColorScheme } from "@mantine/core";
import {
  AuthSignupPost201Response,
  MasterApiUsersUserIdDeleteRequest
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import type { Column, SortColumn } from "react-data-grid";
import DataGrid, { SelectColumn } from "react-data-grid";
import { useMutation, useQuery } from "react-query";
import { UsersDetailForm } from "../form/UsersDetailForm";

interface ColorThemeProps {
  isDarkMode: boolean;
}

export const UsersTable = () => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);

  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";

  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const [selectedRows, setSelectedRows] = useState(
    (): ReadonlySet<any> => new Set()
  );

  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });

  const { openModal } = useModal();

  const { data: user, refetch } = useQuery(users.get({
    query: query,
    search: search.search,
    searchFields: search.searchFields.length
      ? search.searchFields
      : usersHeader.map((user) => user.value),
    page: activePage,
    pageSize: Number(search.pageSize),
    sort: sort.length ? sort.join(",") : "-id",
  }));

  const updateUrlParams = useUpdateUrlParams();

  // 새로운 데이터가 로드될 때, 현재 페이지가 유효하지 않으면 1페이지로 설정
  useEffect(() => {
    if (user && activePage > (user?.data?.totalPages ?? 0)) {
      setPage(1);
      setQueryString((prev) => ({
        ...prev,
        page: 1,
      }));
    }
  }, [user, activePage, setQueryString]);

  // 페이지 Query String으로 공정 이동
  useEffect(() => {
    updateUrlParams({
      page: activePage,
      pageSize: search.pageSize,
      search: search.search,
      searchFields: search.searchFields,
      query: JSON.stringify(query),
      sort: sort
    }, queryDefaultValues);
  }, [activePage, search, query, sort, updateUrlParams]);

  const rows: readonly AuthSignupPost201Response[] = user?.data?.rows ?? [];

  const { mutate: deleteMutate } = useMutation(
    (params: MasterApiUsersUserIdDeleteRequest) =>
      mutateUsers.delete(params).mutationFn(params as MasterApiUsersUserIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );
  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());
  const columns: readonly Column<AuthSignupPost201Response>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "code",
      name: "사용자 코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink onClick={() => openModal(<UsersDetailForm UserCode={formatterProps.row.code} />, null, "")}>{formatterProps.row.code}</DetailLink>
        )
      },
    },
    {
      key: "name",
      name: "사용자명",
      sortable: true,
      resizable: true,
    },
    {
      key: "username",
      name: "로그인아이디",
      sortable: true,
      resizable: true,
    },
    {
      key: "equipmentCode",
      name: "설비코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink onClick={() => openModal(<EquipmentDetailForm equipmentCode={formatterProps.row.equipmentCode} />, null, "")}>{formatterProps.row.equipmentCode}</DetailLink>
        )
      },
    },
    {
      key: "locationCode",
      name: "로케이션코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink onClick={() => openModal(<LocationDetailForm locationCode={formatterProps.row.locationCode} />, null, "")}>{formatterProps.row.locationCode}</DetailLink>
        )
      },
    },
    {
      key: "printerCode",
      name: "프린터 코드",
      sortable: true,
      resizable: true,
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <UsersUpdateForm formatterProps={formatterProps.row} />,
                  {},
                  "사용자 업데이트",
                  true
                );
              }}
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                window.confirm("정말로 삭제하시겠습니까?") &&
                  deleteMutate({
                    userId: formatterProps?.row?.id!,
                  });
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  useSub("deleteSelectedItems", () => {
    const rows = Array.from(selectedRowsRef.current);
    if (!rows.length) return;
    const deleteRows: number[] = Array.from(rows[rows.length - 1].values());
    if (window.confirm("선택한 행을 삭제하시겠습니까?")) {
      deleteRows.forEach((row: number) => {
        deleteMutate({ userId: row });
      });
      selectedRowsRef.current = new Set();
    }
  });

  return (
    <UsersTableWrapper>
      <GridWrapper isDarkMode={isDarkMode}>
        <TableWrapper>
          <RetrieveWrapper>
            <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
              <CustomFilter filterType={usersHeader} setQuery={setQuery} query={query} />
              <CustomSorter sorterType={usersHeader} setSort={setSort} sort={sort} />
            </Flex>
            <SearchBox searchType={usersHeader} setSearch={setSearch} search={search} />
          </RetrieveWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={rows ?? []}
            rowHeight={40}
            rowKeyGetter={(row: AuthSignupPost201Response) => row.id}
            sortColumns={sortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
            }}
          />
        </TableWrapper>
        <PaginationWrapper>
          <Pagination
            onChange={setPage}
            value={activePage}
            total={user?.data?.totalPages ?? 0}
            size="lg"
            radius="sm"
          />
        </PaginationWrapper>
      </GridWrapper>
    </UsersTableWrapper>
  );
};

const UsersTableWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const GridWrapper = styled.div<ColorThemeProps>`
  & *[role="grid"] {
    height: 440px;
    --rdg-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-header-background-color: ${(props) =>
    props.isDarkMode ? "white" : "none"};
    --rdg-color: ${(props) => (props.isDarkMode ? "black" : "white")};
    --rdg-row-hover-background-color: ${(props) =>
    props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;
const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;
const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const RetrieveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  justify-content: space-between;
`;
